import React, {useEffect, useState} from 'react';
import {NavLink, useHistory, useLocation} from 'react-router-dom';
import {Box, Collapse, Divider, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography} from '@mui/material';
import {Add, ExpandLess, ExpandMore} from '@mui/icons-material';
import {styled} from '@mui/system';
import {alpha} from '@mui/material/styles';
import {getNavigationGroups, isNavigationActive} from './navigation';

export const CustomListItem = styled(ListItemButton)(({theme}) => {
    return ({
        "&:hover": {
            backgroundColor: theme.palette.background.rowHover
        },
        "&.Mui-selected": {
            backgroundColor: theme.palette.action.selected,
            color: theme.palette.primary.light,
            "&:hover": {
                backgroundColor: theme.palette.background.rowHover
            },
        },
    })
});

export function SidebarNavigationItem({item, expanded, nested = false, selected, onClick, ...props}) {
    const Icon = item.icon;
    return (
        <Tooltip title={expanded ? '' : item.label} placement="right" arrow>
            <ListItemButton {...props} selected={selected} onClick={onClick} aria-label={item.label}
                sx={{minHeight: 36, px: expanded ? 1.25 : 0, py: 0.5, mx: 1.25, my: 0.25, gap: 1.25,
                    justifyContent: expanded ? 'flex-start' : 'center', borderRadius: '8px',
                    color: selected ? 'primary.main' : 'text.secondary',
                    ...(nested && {ml: 2.75, borderLeft: 1, borderColor: 'divider', borderRadius: '0 8px 8px 0'}),
                    '&:hover': {bgcolor: 'background.rowHover', color: 'text.primary'},
                    '&.Mui-selected': {bgcolor: theme => alpha(theme.palette.primary.main, 0.12), color: 'primary.main',
                        '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.18)}},
                    '&.Mui-focusVisible': {outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2}}}>
                <ListItemIcon sx={{minWidth: 20, color: 'inherit', justifyContent: 'center'}}><Icon sx={{fontSize: nested ? 18 : 20}}/></ListItemIcon>
                {expanded && <>
                    <ListItemText primary={item.label} sx={{my: 0}} slotProps={{primary: {sx: {fontSize: nested ? 12 : 13, fontWeight: selected ? 600 : 500}}}}/>
                    {item.badge && <Box component="span" sx={{px: 0.75, py: 0.25, borderRadius: '4px', fontSize: 9, fontWeight: 600,
                        color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.1)}}>{item.badge}</Box>}
                    {item.children && (props['aria-expanded'] ? <ExpandLess sx={{fontSize: 16}}/> : <ExpandMore sx={{fontSize: 16}}/>)}
                </>}
            </ListItemButton>
        </Tooltip>
    );
}

export function MainListItems({auth, sidebar, expanded, visible, onNavigate, onExpand, handleIsLogsOpen, onSectionEntered}) {
    const location = useLocation();
    const history = useHistory();
    const groups = getNavigationGroups(auth?.userSettings?.role);
    const settingsItems = groups.find(group => group.title === 'Account').items[0].children;
    const settingsActive = settingsItems.some(item => isNavigationActive(item, location.pathname));
    const [settingsOpen, setSettingsOpen] = useState(settingsActive);

    useEffect(() => {
        if (settingsActive) setSettingsOpen(true);
    }, [settingsActive]);

    useEffect(() => {
        if (location.pathname.startsWith('/log/')) handleIsLogsOpen(true);
    }, [location.pathname, handleIsLogsOpen]);

    useEffect(() => {
        if (expanded && visible) onSectionEntered();
    }, [location.pathname, expanded, visible, settingsOpen, sidebar.isLogsOpen, onSectionEntered]);

    const toggleSection = item => {
        if (!expanded) onExpand();
        if (item.key === 'logs') handleIsLogsOpen(!expanded || !sidebar.isLogsOpen);
        else setSettingsOpen(!expanded || !settingsOpen);
    };
    const link = (item, nested = false) => (
        <SidebarNavigationItem key={item.to} item={item} expanded={expanded} nested={nested}
            component={NavLink} to={item.to} isActive={() => isNavigationActive(item, location.pathname)}
            selected={isNavigationActive(item, location.pathname)} onClick={onNavigate}/>
    );

    const renderGroup = (group, index) => (
        <Box key={group.title} sx={{mb: 1}}>
            {expanded ? <Typography component="h2" sx={{px: 2.5, pt: index ? 1 : 0, pb: 0.75, fontSize: 10,
                fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'text.disabledLight'}}>{group.title}</Typography>
                : index > 0 && <Divider sx={{mx: 2.5, my: 1.25}}/>}
            <List component="div" disablePadding>
                {group.items.map(item => {
                    if (!item.children) return link(item);
                    const open = item.key === 'logs' ? sidebar.isLogsOpen : settingsOpen;
                    const active = item.children.some(child => isNavigationActive(child, location.pathname));
                    return <React.Fragment key={item.key}>
                        <SidebarNavigationItem item={item} expanded={expanded} selected={active && (!expanded || !open)}
                            onClick={() => toggleSection(item)} aria-expanded={expanded && open} aria-controls={`sidebar-${item.key}`}/>
                        <Collapse in={expanded && open} unmountOnExit onEntered={onSectionEntered}>
                            <List component="div" disablePadding id={`sidebar-${item.key}`} aria-label={item.label}>
                                {item.children.map(child => link(child, true))}
                            </List>
                        </Collapse>
                    </React.Fragment>;
                })}
            </List>
        </Box>
    );

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0}}>
            <Box sx={{flexShrink: 0}}>
                <Tooltip title={expanded ? '' : 'New Campaign'} placement="right" arrow>
                    <ListItemButton aria-label="New Campaign" onClick={() => {
                        history.push({pathname: '/campaigns/new', state: {background: {pathname: '/campaigns'}}});
                        onNavigate();
                    }} sx={{mx: 1.25, mb: 1.5, minHeight: 38, px: 1.25, justifyContent: expanded ? 'flex-start' : 'center',
                        border: 1, borderColor: theme => alpha(theme.palette.primary.main, 0.25), borderRadius: '8px',
                        color: 'primary.main', bgcolor: theme => alpha(theme.palette.primary.main, 0.08), gap: 1.25,
                        '&:hover': {bgcolor: theme => alpha(theme.palette.primary.main, 0.15)}}}>
                        <Add sx={{fontSize: 20}}/>
                        {expanded && <Typography sx={{fontSize: 13, fontWeight: 600}}>New Campaign</Typography>}
                    </ListItemButton>
                </Tooltip>
                {renderGroup(groups[0], 0)}
            </Box>
            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin'}}>
                {groups.slice(1).map((group, index) => renderGroup(group, index + 1))}
            </Box>
        </Box>
    );
}
